import { useState, useCallback } from 'react';
import { toast } from 'sonner';

const VALID_FILE_TYPES = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Custom hook for file upload handling with drag & drop
 */
export const useFileUpload = () => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [attachments, setAttachments] = useState([]);

  const validateFile = useCallback((file) => {
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!VALID_FILE_TYPES.includes(fileExtension)) {
      toast.error('Ugyldig filtype. Bare PDF, Word og bilder er tillatt.');
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Filen er for stor. Maksimal st\u00f8rrelse er 10MB.');
      return false;
    }
    return true;
  }, []);

  const handleFileSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setUploadedFile(file);
      toast.success('Fil lastet opp!');
    }
  }, [validateFile]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file && validateFile(file)) {
      setUploadedFile(file);
      toast.success('Fil lastet opp!');
    }
  }, [validateFile]);

  const clearFile = useCallback(() => {
    setUploadedFile(null);
    toast.info('Fil fjernet');
  }, []);

  return {
    uploadedFile,
    setUploadedFile,
    isDragging,
    attachments,
    setAttachments,
    handleFileSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    clearFile
  };
};

export default useFileUpload;
